import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Loader2, Save, ChevronDown } from "lucide-react";
import { apiGet, apiPut } from "../../api";

const formatTypeLabel = (type) =>
  type
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const CustomSelect = ({ options, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = value ? formatTypeLabel(value) : "Select...";

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 disabled:opacity-50"
      >
        <span className="truncate text-left">{selectedLabel}</span>
        <ChevronDown
          className={`ml-2 h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-border/70 bg-card shadow-xl">
          {options.map((opt) => (
            <li
              key={opt.type}
              onClick={() => {
                onChange(opt.type);
                setOpen(false);
              }}
              className={`px-3 py-2 text-sm cursor-pointer text-foreground hover:bg-white/[0.05] ${
                opt.type === value ? "bg-ginox-green/10 text-ginox-green font-medium" : ""
              }`}
            >
              {formatTypeLabel(opt.type)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ValuesPage = () => {
  const [valueTypes, setValueTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [valueData, setValueData] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [editedValue, setEditedValue] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchValueTypes();
  }, []);

  useEffect(() => {
    if (selectedType) fetchValue();
  }, [selectedType]);

  useEffect(() => {
    if (valueData) {
      setEditedValue(valueData.value || "");
      setHasChanges(false);
    }
  }, [valueData]);

  const fetchValueTypes = async () => {
    setTypesLoading(true);
    try {
      const res = await apiGet("/api/admin/general/values-list");
      if (res.data?.success) {
        const list = res.data.data || [];
        setValueTypes(list);
        if (list.length > 0) setSelectedType(list[0].type);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch value types");
    } finally {
      setTypesLoading(false);
    }
  };

  const fetchValue = async () => {
    setLoading(true);
    setValueData(null);
    try {
      const res = await apiGet(`/api/admin/general/values/${selectedType}`);
      if (res.data?.success) {
        setValueData(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch value");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    if (field === "value") {
      setEditedValue(value);
      setHasChanges(value !== String(valueData?.value ?? ""));
    }
  };

  const handleUpdateValue = async () => {
    if (!editedValue) {
      toast.error("Value is required");
      return;
    }
    setSaving(true);
    try {
      const res = await apiPut(`/api/admin/general/values/${valueData._id}`, {
        value: editedValue,
        description: valueData.description,
      });
      if (res.data?.success) {
        toast.success(res.data.message || "Value updated successfully!");
        fetchValue();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update value");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";
  const disabledInputCls = "w-full rounded-md border border-border/40 bg-white/[0.01] px-3 py-2 text-sm text-muted-foreground opacity-60 cursor-not-allowed";
  const labelCls = "block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5";

  return (
    <div>
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        System
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Points <span className="text-gradient-ginox">Management</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Configure system value types and their amounts
      </p>

      <div className="mb-6 w-full md:w-96">
        <label className={labelCls}>Select Value Type</label>
        {typesLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading types...
          </div>
        ) : (
          <CustomSelect
            options={valueTypes}
            value={selectedType}
            onChange={setSelectedType}
          />
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ginox-green" />
        </div>
      ) : valueData ? (
        <div className="border-t border-white/[0.08] pt-6">
          <h2 className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60 mb-5">
            {selectedType ? formatTypeLabel(selectedType) : ""}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Type</label>
              <input
                type="text"
                value={valueData.type || ""}
                disabled
                className={disabledInputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Value *</label>
              <input
                type="text"
                value={editedValue}
                onChange={(e) => handleFieldChange("value", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Description</label>
              <input
                type="text"
                value={valueData.description || ""}
                disabled
                className={disabledInputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Created At</label>
              <input
                type="text"
                value={valueData.createdAt ? new Date(valueData.createdAt).toLocaleString() : ""}
                disabled
                className={disabledInputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Updated At</label>
              <input
                type="text"
                value={valueData.updatedAt ? new Date(valueData.updatedAt).toLocaleString() : ""}
                disabled
                className={disabledInputCls}
              />
            </div>
          </div>

          <button
            onClick={handleUpdateValue}
            disabled={saving || !hasChanges}
            className="mt-5 inline-flex items-center gap-2 rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save Changes
          </button>
        </div>
      ) : (
        !loading && selectedType && (
          <p className="text-sm text-muted-foreground">No data available</p>
        )
      )}
    </div>
  );
};

export default ValuesPage;
